import { SESSION_EXPIRED_STORAGE_KEY } from '#shared/constants/auth';

/**
 * Composable de autenticación para el cliente
 *
 * Utiliza nuxt-auth-utils para gestionar la sesión.
 * Los tokens se manejan exclusivamente en el servidor (patrón BFF).
 * El cliente solo conoce la información pública del usuario.
 */

export const useAuth = () => {
  // useUserSession es proporcionado por nuxt-auth-utils
  const { user, session, fetch: fetchSession } = useUserSession();

  // Estado de carga para operaciones de autenticación
  const isLoading = useState('auth-loading', () => false);

  /**
   * Inicia el flujo de autenticación
   * Redirige al servidor para iniciar OIDC
   */
  const login = async (): Promise<void> => {
    isLoading.value = true;
    // Navegar a la ruta de login del servidor
    // external: true es necesario porque es una ruta de Nitro, no de Vue Router
    await navigateTo('/auth/login', { external: true });
  };

  /**
   * Cierra la sesión del usuario
   * Redirige al servidor para limpiar sesión y logout OIDC
   */
  const logout = async (): Promise<void> => {
    isLoading.value = true;
    // Navegar a la ruta de logout del servidor
    await navigateTo('/auth/logout', { external: true });
  };

  /**
   * Refresca la información de sesión desde el servidor
   * Útil después de cambios en el perfil del usuario
   */
  const refreshSession = async (): Promise<void> => {
    try {
      await fetchSession();
    } catch (error) {
      // oxlint-disable-next-line no-console
      if (import.meta.dev) console.error('Error al refrescar sesión:', error);
    }
  };

  /**
   * Obtiene información del usuario
   * Solo devuelve datos públicos del perfil
   */
  const getUserInfo = () => {
    return user.value || null;
  };

  /**
   * Obtiene la fecha de inicio de sesión
   */
  const getLoggedInAt = () => {
    return session.value?.loggedInAt || null;
  };

  /**
   * Marca en sessionStorage que la sesión caducó y redirige transparentemente al login.
   * El aviso se muestra al aterrizar de nuevo en /admin una vez completado el flujo OIDC.
   */
  const handleNonRecoverableAuth = async (_options?: { reason?: string }): Promise<void> => {
    if (import.meta.client) {
      sessionStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, '1');
    }
    await navigateTo('/auth/login', { external: true });
  };

  return {
    // Estado reactivo
    user: readonly(user),
    isLoading: readonly(isLoading),
    session: readonly(session),

    // Acciones
    login,
    logout,
    refreshSession,
    handleNonRecoverableAuth,

    // Helpers
    getUserInfo,
    getLoggedInAt,
  };
};
